import React from "react";
import type { LayoutProps } from "styled-system";
import { useModelFilterDispatch, useModelFilterSelector } from "../../../redux/store";
import * as Styles from "./styles/ModelResultStyles";
import { ModelFilterLabel } from "../../../utils/labels";
import type { ModelResultType } from "../../../../../../shared-logic/features/filters/utils/constants/resultConstants";
import { SrOnly } from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { trackSelectModel, trackSelectResult } from "../../../utils/tracking";
import { getModelMapValueById, isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getBuildUrlBasedOnNewCarResult, getModelResultImage } from "../../../utils/helpers";
import { ImageFluid } from "../../../../../../common-deprecated/styles/v2/toyota/globals/Image";
import { getGrLogo, redirect } from "../../../../../../common-deprecated/utils";
import GRLogo from "../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import ModelResultPrice from "./price/ModelResultPrice";
import { showPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/toyota/ButtonTertiary";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/toyota/availability-badges/AvailabilityBadges";
import ButtonQuaternary from "../../../../../../common-deprecated/components/aem/toyota/ButtonQuaternary";
import { Ellipsis } from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Ellipsis";
import EcoTags from "./eco-tags/EcoTags";
import EcoLabel from "../common/EcoLabel";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import { CommonLabel, getCommonOptionalLabel } from "../../../../../../common-deprecated/utils/commonLabels";
import useModelResultTags from "../../../hooks/useModelResultTags";

type PropsType = {
    model: ModelResultType;
    hasRightBorder?: boolean;
};

const ModelResult = ({ model, hasRightBorder }: PropsType & LayoutProps): JSX.Element => {
    const dispatch = useModelFilterDispatch();
    const commonSettings = useModelFilterSelector((state) => state.commonSettings);
    const modelFilterSettings = useModelFilterSelector((state) => state.modelFilterSettings);
    const { isStandalone } = commonSettings;
    const { id, name, cars, cheapestCar = cars[0] } = model;
    const internalCode = Object.values(commonSettings.modelMap).find((item) => item.id === id)?.internalCode ?? "";

    const modelData = useModelFilterSelector((state) => state.modelFilterSettings.pageUrlsModelData);
    const {
        modelIsAvailableForOnlineSale,
        modelIsAvailableForOnlineLeasing,
        modelIsSoldOut,
        ecoTags,
        ecoBonus,
        ecoBadgeUrls,
    } = useModelResultTags({ model });

    const specificModelData = modelData.find(
        (modelDataModel) => modelDataModel.modelCode.toLowerCase() === cheapestCar.model.code.toLowerCase(),
    );

    const etaTooltip = getCommonOptionalLabel({ commonSettings }, "etaTooltip");
    const etaTooltipTitle = getCommonOptionalLabel({ commonSettings }, "etaTooltipTitle");

    const { links } = getModelMapValueById(commonSettings, id) || {};
    const grLogoCheck = getGrLogo(name, "");

    const shouldShowPrice = showPrice(commonSettings);

    // IMPORTANT -> For this CCIS image there are some caveats to take into accounts, see the comment on the getCarImageVisualForModel method.
    const carImage = getModelResultImage(commonSettings, model, 4, {
        type: "png",
        width: 390,
        height: 260,
        // This is providing negative padding (top) in order to force the returned dimensions of the image to match the designs.
        // No current issues are known using this method, however I would advise to use this with caution.
        padding: "0,0,-50,30",
        scaleMode: 3,
    });

    // URL for standalone link to build and buy
    const standaloneBuildUrl = getBuildUrlBasedOnNewCarResult(cheapestCar, commonSettings, modelData);
    const modelPageUrl = (isStandalone ? standaloneBuildUrl : links?.modelPage) || "";
    const buildPageUrl = (isStandalone ? standaloneBuildUrl : links?.buildPage) || "";
    // ! IF this changes to the carconfig again, keep speedboat in mind
    const configureCtaUrl =
        !isStandalone && specificModelData?.apheleiaLiveStatusWebsite
            ? `${modelPageUrl}#aphgradeselector`
            : buildPageUrl;

    const imgAltDescr =
        cheapestCar.grade.name === "" || cheapestCar.bodyType.name === ""
            ? `${name} ${cheapestCar.grade.ecoTag ? cheapestCar.grade.ecoTag : ""} ${cheapestCar.engine.name}`
            : `${name} ${cheapestCar.grade.name} ${cheapestCar.bodyType.name}`;

    return (
        <Styles.Wrapper hasRightBorder={hasRightBorder}>
            <Styles.HeaderWrapper data-url={modelPageUrl}>
                <Styles.Header>
                    <Styles.Title>
                        <Styles.RedirectWrapper
                            onClick={(event) => {
                                event.preventDefault();
                                redirect(modelPageUrl);
                                trackSelectModel(name);
                            }}
                            href={modelPageUrl}
                            data-url={modelPageUrl}
                            aria-labelledby={`viewmodel-${id} viewmodel-${internalCode}`}
                            data-testid={`viewmodel-${id} viewmodel-${internalCode}`}
                        >
                            {name}
                        </Styles.RedirectWrapper>
                    </Styles.Title>

                    {shouldShowPrice && <ModelResultPrice model={model} />}
                    <EcoBonusPromotion ecoBonus={ecoBonus} />

                    {modelFilterSettings.showETA && model.cars[0].eta && (
                        <Styles.Eta
                            onClick={
                                etaTooltip
                                    ? (event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          dispatch(
                                              showModal({
                                                  type: MODAL_CONTENT,
                                                  content: etaTooltip,
                                                  title: etaTooltipTitle || "",
                                              }),
                                          );
                                      }
                                    : undefined
                            }
                        >
                            <p>{model.cars[0].eta}</p>

                            {etaTooltip && (
                                <Styles.Eta>
                                    <SrOnly>
                                        <CommonLabel label="ariaToggleEtaTooltip" />
                                    </SrOnly>
                                    <Styles.DisclaimerIcon>
                                        <Icon variant="info" aria-hidden="true" />
                                    </Styles.DisclaimerIcon>
                                </Styles.Eta>
                            )}
                        </Styles.Eta>
                    )}
                    <Styles.BadgesWrapper>
                        <EcoTags ecoTags={Array.from(ecoTags)} />
                        <AvailabilityBadges
                            isSoldOut={modelIsSoldOut}
                            isAvailableForOnlineSales={modelIsAvailableForOnlineSale}
                            isAvailableForOnlineLeasing={modelIsAvailableForOnlineLeasing}
                        />
                    </Styles.BadgesWrapper>

                    <Styles.EcoBadgeWrapper>
                        {grLogoCheck && <Styles.GRLogo>{grLogoCheck ? <GRLogo /> : <GRLogoSport />}</Styles.GRLogo>}
                        {ecoBadgeUrls.map((url) => url && <EcoLabel url={url} />)}
                    </Styles.EcoBadgeWrapper>
                </Styles.Header>
                <Styles.ImageWrapper>
                    <Styles.RedirectWrapper
                        onClick={(event) => {
                            event.preventDefault();
                            redirect(modelPageUrl);
                            trackSelectModel(name);
                        }}
                        href={modelPageUrl}
                    >
                        <ImageFluid src={carImage} width={300} alt={imgAltDescr} loading="lazy" />
                    </Styles.RedirectWrapper>
                </Styles.ImageWrapper>
            </Styles.HeaderWrapper>
            <Styles.Actions>
                <ButtonTertiary
                    onClick={(event) => {
                        event.preventDefault();
                        redirect(modelPageUrl);
                        trackSelectModel(name);
                    }}
                    href={modelPageUrl}
                    data-url={modelPageUrl}
                    aria-labelledby={`viewmodel-${id} viewmodel-${internalCode}`}
                    data-testid={`viewmodel-${id} viewmodel-${internalCode}`}
                >
                    <SrOnly id={`viewmodel-${id} viewmodel-${internalCode}`}>{name}:</SrOnly>
                    <ModelFilterLabel label="viewModel" />
                </ButtonTertiary>
                <ButtonQuaternary
                    onClick={(event) => {
                        event.preventDefault();
                        redirect(configureCtaUrl);
                        trackSelectResult(name);
                    }}
                    href={configureCtaUrl}
                    hasIconRight
                    data-url={configureCtaUrl}
                    aria-labelledby={`viewgrades-${id}`}
                >
                    <Icon variant={isRTL(commonSettings) ? "arrowLeft" : "arrowRight"} />
                    <Ellipsis maxWidth="100%">
                        <SrOnly>{name}:</SrOnly>
                        <ModelFilterLabel label="goToBuild" />
                    </Ellipsis>
                </ButtonQuaternary>
            </Styles.Actions>
        </Styles.Wrapper>
    );
};

export default ModelResult;
